import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { onboardingQuestionnaireActions } from '../../../../store';
import { documentNeedsDetails } from './utils';

import type { AppDispatch, RootState } from '../../../../store';

export default function useFetchOnboardingDocumentDetails() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { onboardingQuestionnaireId } = useParams<'onboardingQuestionnaireId'>();
  const documents = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.onboardingQuestionnaireDetails?.documents,
  );
  const questionnaireDetailsLoading = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.questionnaireDetailsLoading,
  );
  const fetchedDocumentIdsRef = useRef<Set<number>>(new Set());
  const inFlightIdsRef = useRef<Set<number>>(new Set());
  const inFlightAbortsRef = useRef<Set<() => void>>(new Set());

  // Abort in-flight requests only on unmount or questionnaire change — never on documents updates.
  useEffect(() => {
    const inFlightAborts = inFlightAbortsRef.current;
    fetchedDocumentIdsRef.current.clear();
    inFlightIdsRef.current.clear();
    return () => {
      inFlightAborts.forEach((abort) => abort());
      inFlightAborts.clear();
    };
  }, [onboardingQuestionnaireId]);

  // A details refetch or upload can strip signed URLs; allow those IDs to be fetched again.
  useEffect(() => {
    if (questionnaireDetailsLoading) {
      fetchedDocumentIdsRef.current.clear();
    }
  }, [questionnaireDetailsLoading]);

  useEffect(() => {
    if (!documents) {
      return;
    }
    for (const document of documents) {
      if (documentNeedsDetails(document) && !inFlightIdsRef.current.has(document.id)) {
        fetchedDocumentIdsRef.current.delete(document.id);
      }
    }
  }, [documents]);

  useEffect(() => {
    if (!onboardingQuestionnaireId || questionnaireDetailsLoading || !documents) {
      return;
    }

    let cancelled = false;
    const { getOnboardingQuestionnaireDocumentDetails } = onboardingQuestionnaireActions;

    void (async () => {
      for (const document of documents) {
        if (cancelled || !documentNeedsDetails(document)) {
          continue;
        }

        if (
          fetchedDocumentIdsRef.current.has(document.id) ||
          inFlightIdsRef.current.has(document.id)
        ) {
          continue;
        }

        fetchedDocumentIdsRef.current.add(document.id);
        inFlightIdsRef.current.add(document.id);

        const request = dispatch(
          getOnboardingQuestionnaireDocumentDetails({
            onboardingQuestionnaireId,
            documentId: String(document.id),
            getAccessTokenSilently,
          }),
        );
        const abort = () => request.abort();
        inFlightAbortsRef.current.add(abort);
        const result = await request;
        inFlightAbortsRef.current.delete(abort);
        inFlightIdsRef.current.delete(document.id);

        if (getOnboardingQuestionnaireDocumentDetails.rejected.match(result)) {
          fetchedDocumentIdsRef.current.delete(document.id);
        }

        if (cancelled) {
          return;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    documents,
    getAccessTokenSilently,
    onboardingQuestionnaireId,
    questionnaireDetailsLoading,
  ]);
}
